/**
 * Widget module
 * @module @moreonion/socialproof/widget
 */

/**
 * Base widget for displaying polled data.
 */
export default class Widget {
  /**
   * Default settings
   * @type {object}
   */
  static defaults = {
    pathToValue: '',
    styleProp: null,
    htmlAttr: null,
    formatter: null,
    pollLimit: null,
  }

  /**
   * Most recent unformatted value
   * @type {*}
   */
  static value = null

  /**
   * Create a widget.
   * @param {HTMLElement} element - The widget’s HTML element
   * @param {object} [options] - Options for the widget
   * @param {string} [options.pathToValue=''] - JSON path from which to read the data
   * @param {string|null} [options.styleProp=null] - CSS property to update instead of the content
   * @param {string|null} [options.htmlAttr=null] - HTML attribute to update instead of the content
   * @param {function|null} [options.formatter=null] - Function to modify the output of a value
   * @param {number|null} [options.pollLimit=null] - Limit to the number of polls
   */
  constructor (element, options = {}) {
    /**
     * HTML element representing the widget
     * @type {HTMLElement}
     */
    this.element = element
    /**
     * Instance settings
     * @type {object}
     */
    this.settings = {
      ...Widget.defaults,
      ...options,
      ...element.dataset,
    }
    // Cast values from dataset to an appropriate type.
    this.settingsToNumber('pollLimit')
    if (typeof this.settings.formatter === 'string') {
      console.warn('Socialproof does currently not support formatter functions passed as string.')
      this.settings.formatter = Widget.defaults.formatter
    }
  }

  /**
   * Cast string values of a list of setting names to a number.
   *
   * @param {...string} settings - Names of settings to check
   */
  settingsToNumber (...settings) {
    for (const setting of settings) {
      if (typeof this.settings[setting] === 'string') {
        this.settings[setting] = parseFloat(this.settings[setting])
      }
    }
  }

  /**
   * Get the value from the path configured in the settings.
   *
   * @param {object} data - Plain object
   *
   * @returns {*} The found value
   */
  getValue (data) {
    if (!this.settings.pathToValue) {
      return data
    }
    return this.settings.pathToValue.split('.').reduce(
      (accumulator, current) => accumulator?.[current],
      data
    )
  }

  /**
   * Format the value as configured in the settings.
   *
   * @param {*} value - The value to format
   *
   * @returns {*} The formatted value
   */
  format (value) {
    if (typeof this.settings.formatter === 'function') {
      return this.settings.formatter(value)
    }
    return value
  }

  /**
   * Update the widget’s HTML.
   *
   * @param {string|number} value - The new value
   */
  update (value) {
    this.value = value
    if (this.settings.styleProp) {
      this.element.style[this.settings.styleProp] = this.format(value)
    } else if (this.settings.htmlAttr) {
      this.element.setAttribute(this.settings.htmlAttr, this.format(value))
    } else {
      this.element.textContent = this.format(value)
    }
  }

  /**
   * Poll data from an external source and update the widget.
   *
   * @param {Poller} poller - A Poller object
   *
   * @returns {rxjs.Subscription} The poll subscription
   */
  poll (poller) {
    return poller.subscribe({
      next: (data) => {
        const newValue = this.getValue(data)
        if (typeof newValue !== 'undefined') {
          this.update(newValue)
        } else {
          console.warn(`Socialproof polling returned no data for "${this.settings.pathToValue}".`)
        }
      },
      error: (error) => {
        console.warn('Socialproof polling failed.', { error: error.cause || error })
      }
    }, this.settings.pollLimit || Infinity)
  }
}
